import { Map } from "immutable";
import { isString } from "lodash";
import { createSelector } from "reselect";

import searchableFields from "../../constants/searchableFields";
import { appStateSelectors } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { typeSelectors } from "../../modules/definition/types";
import displayCacheSelectors from "../../modules/display/selectors";
import { uiStateSelectors } from "../../modules/uiState";
import { immutableRecords } from "../../types";

const itemMatchesRegEx = (obj, regex, excludedTypes, fields, propertiesById) => {
  return (
    !excludedTypes.contains(obj.type) &&
    fields.some(f => {
      if (f === "propertyValues") {
        return obj.propertyValues.some((v, k) => {
          const isTextField = ["text", "multitext", "list"].includes(propertiesById.get(k).format);
          return isTextField && isString(v) ? regex.test(v) : false;
        });
      }
      return regex.test(obj.get(f));
    })
  );
};

const getSearchFieldsAfterExclusion = excludedFields =>
  Map(searchableFields)
    .map((name, id) => id)
    .filter(f => !excludedFields.contains(f));

// Get items
const getItemsMatchingSearch = createSelector(
  // inputs
  state => state.data.items.byId,
  uiStateSelectors.getSearchValue,
  uiStateSelectors.getSearchCaseSensitive,
  uiStateSelectors.getSearchWholeWord,
  appStateSelectors.getExcludedSearchTypes,
  uiStateSelectors.getExcludedSearchFields,
  definitionSelectors.getPropertiesById,
  // calculate based on inputs
  (itemsById, value, caseSensitive, wholeWord, excludedTypes, excludedFields, propertiesById) => {
    if (!value) {
      return Map();
    }
    const r = wholeWord ? "\\b" + value + "\\b" : value;
    const regex = new RegExp(r, caseSensitive ? "" : "i");
    const fields = getSearchFieldsAfterExclusion(excludedFields);
    return itemsById.filter(obj => itemMatchesRegEx(obj, regex, excludedTypes, fields, propertiesById));
  }
);

// Get display items
/**
 * @param {TimelineRecord} state
 */
const getDisplayItemsMatchingSearch = createSelector(
  // inputs
  getItemsMatchingSearch,
  displayCacheSelectors.getDisplayItemsById,
  typeSelectors.getTypesById,
  // calculate based on inputs
  (matchingItems, displayItems, types) => {
    const typeDisplay = types.map(t => immutableRecords.ItemTypeDisplayValues(t));
    return matchingItems
      .map((item, id) => {
        const displayItem = displayItems.get(id);
        return immutableRecords.SearchResultRecord(displayItem).set("typeData", typeDisplay.get(displayItem.type));
      })
      .toList();
  }
);

export default {
  getDisplayItemsMatchingSearch
};
